import useSWR from 'swr';

import { getCoreApi } from '@/utility/getCoreApi';

import apiRequest from './apiRequest';
import { IFullUserInfo, IUserIterable, IUserNotifications } from './types/user/user';

const getUserData = async (userId?: number, token?: string) => {
  return await apiRequest<IFullUserInfo>(
    {
      url: `${getCoreApi()}/users/${userId}`,
      method: 'GET',
    },
    true,
    true,
    token,
  );
};

export const useUserData = (userId?: number, token?: string) => {
  const { data, error, mutate, isValidating } = useSWR(
    userId ? [`user-${userId}`, token] : null,
    () => getUserData(userId, token),
  );

  const isLoading = !data && !error && isValidating;

  return {
    data: data,
    isError: error,
    mutate,
    isValidating,
    isLoading,
  };
};

const getUserIterableNotifications = async (userId?: string | number) => {
  return await apiRequest<IUserIterable>(
    {
      url: `${getCoreApi()}/users/iterable/${userId}`,
      method: 'GET',
    },
    true,
    false,
  );
};

export const useUserIterableNotifications = (userId?: string | number) => {
  const { data, error, mutate, isValidating } = useSWR(userId ? [`user-${userId}`] : null, () =>
    getUserIterableNotifications(userId),
  );

  const isLoading = !data && !error && isValidating;

  return {
    data: data,
    isError: error,
    mutate,
    isValidating,
    isLoading,
  };
};

const getUpdateUserIterableNotifications = async (
  userId: string | number,
  data?: IUserIterable,
) => {
  return userId && data
    ? apiRequest<IUserIterable>(
        {
          url: `${getCoreApi()}/users/iterable/${userId}`,
          method: 'PATCH',
          data: data,
        },
        true,
        false,
      )
    : null;
};

export const useUpdateUserIterableNotifications = (userId?: string | number) => {
  const { data, error, mutate, isValidating } = useSWR(userId ? [userId] : null, () =>
    userId ? getUpdateUserIterableNotifications(userId) : null,
  );

  const isLoading = !data && !error && isValidating;

  const updateNotifications = async (updateData: IUserIterable) => {
    await mutate(
      async () => (userId ? getUpdateUserIterableNotifications(userId, updateData) : null),
      {
        revalidate: false,
      },
    );
  };

  return {
    data: data,
    isError: error,
    mutate: updateNotifications,
    isValidating,
    isLoading,
  };
};

const getUpdateUserNotifications = async (userId: string | number, data?: IUserNotifications) => {
  return userId && data
    ? apiRequest<IUserNotifications>(
        {
          url: `${getCoreApi()}/users/${userId}/notifications`,
          method: 'PATCH',
          data: data,
        },
        true,
        false,
      )
    : null;
};

export const useUpdateUserNotifications = (userId?: string | number) => {
  const { data, error, mutate, isValidating } = useSWR(userId ? [userId] : null, () =>
    userId ? getUpdateUserNotifications(userId) : null,
  );

  const isLoading = !data && !error && isValidating;

  const updateNotifications = async (updateData: IUserNotifications) => {
    await mutate(async () => (userId ? getUpdateUserNotifications(userId, updateData) : null), {
      revalidate: false,
    });
  };

  return {
    data,
    isError: error,
    mutate: updateNotifications,
    isValidating,
    isLoading,
  };
};
